/**
 * Manages the FCM lifecycle (service worker, token, listeners, logout)
 */
import { ref, readonly } from 'vue';
import { useWebNotification } from '@vueuse/core';
import { deleteToken } from 'firebase/messaging';
import { getFcmToken, getMessagingSafe, onForegroundMessage } from '@/boot/firebase';
import firebaseService from '@/shared/services/firebase.service';

const LS_TOKEN_KEY = '__FCM_TOKEN';
const LS_USER_KEY = '__FCM_USER';
const VITE_FB_VAPID_KEY = import.meta.env.VITE_FB_VAPID_KEY;

// Global unsubscribe for the foreground listener. Stops duplicates.
let foregroundUnsubscribe = null;

export const useFcmSession = () => {
  const token = ref(localStorage.getItem(LS_TOKEN_KEY) || null);
  const userId = ref(localStorage.getItem(LS_USER_KEY) || null);
  const { ensurePermissions, permissionGranted, show } = useWebNotification();

  /**
   * Register the service worker and listen for messages from it.
   *
   * We listen for notification clicks that come from the service worker
   * when the app is already open.
   */
  const ensureServiceWorker = async () => {
    // This is the fix. Registering as a 'module' lets the SW use `import`
    // and stops the conflict with our main app. This is why `onMessage` (foreground) was failing.
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });

    // Listen for messages from the service worker (e.g., notification clicks)
    const messageListener = (event) => {
      console.log('[FCM Session] Message from service worker:', event.data);

      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const url = event.data.url;
        const entityUnique = event.data.entityUnique;

        console.log('[FCM Session] Received NOTIFICATION_CLICK from SW:', { url, entityUnique });

        handleNotificationClick(url, entityUnique);
      }
    };

    navigator.serviceWorker.addEventListener('message', messageListener);

    // Make sure it's actually running before we continue.
    await navigator.serviceWorker.ready;
    return registration;
  };

  /**
   * Listen for messages *while the app is open* (foreground only).
   *
   * Backend sends DATA-ONLY messages, so we extract notification info from payload.data
   */
  const attachForegroundHandler = async () => {
    if (foregroundUnsubscribe) {
      return; // Already attached
    }
    console.log('[FCM Session] Attaching foreground message handler...');

    foregroundUnsubscribe = await onForegroundMessage(async (payload) => {
      console.log('[FCM Session] Foreground message received:', payload);

      // Extract notification details from DATA payload
      const title = payload.data?.title ?? 'New Message';
      const body = payload.data?.body ?? '';
      const icon = payload.data?.icon || '/favicon.ico';
      const url = payload.data?.url || '/';
      const entityUnique = payload.data?.entityUnique;

      const notificationOptions = {
        title,
        body,
        icon,
        tag: payload.messageId,
        data: {
          urlToOpen: url,
          entityUnique: entityUnique,
          messageId: payload.messageId,
        },
      };

      console.log('[FCM Session] Showing foreground notification:', notificationOptions);

      // Show a local "toast" notification
      // Note: useWebNotification returns notification instance
      const notification = await show(notificationOptions);

      // Handle notification click if notification API is supported
      if (notification) {
        // For Notification API (when using native browser notifications)
        notification.onclick = () => {
          console.log('[FCM Session] Notification clicked (native):', url);
          handleNotificationClick(url, entityUnique);
          notification.close();
        };
      }
    });
  };

  /**
   * Handle notification click navigation
   */
  const handleNotificationClick = (url, entityUnique) => {
    console.log('[FCM Session] Handling notification click:', { url, entityUnique });

    if (!url) {
      console.warn('[FCM Session] No URL provided, ignoring click');
      return;
    }

    // Check if it's an external URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      console.log('[FCM Session] Opening external URL:', url);
      window.open(url, '_blank');
    } else {
      // Internal route - use router
      console.log('[FCM Session] Navigating to internal route:', url);
      // Can be customized based on your routing needs
      window.location.href = url;
    }
  };

  /**
   * Main init function. Call this on app start.
   */
  const initializePush = async ({ askPermission = true } = {}) => {
    try {
      await ensureServiceWorker();

      console.log('[FCM Session] Service worker ready.');
      console.log('[FCM Session] Notification permission granted ?', permissionGranted.value);
      // Ask for permission if we don't have it
      if (askPermission && !permissionGranted.value) {
        const permissionOK = await ensurePermissions();
        if (!permissionOK) {
          throw new Error('Notification permission not granted');
        }
      }

      // Get the FCM token
      const newToken = await getFcmToken(VITE_FB_VAPID_KEY);
      if (!newToken) {
        throw new Error('Failed to obtain FCM token');
      }

      const oldToken = token.value;

      console.log('[FCM Session] FCM token obtained:', newToken);

      if (oldToken) {
        console.log('[FCM Session] Existing FCM token found.', oldToken);
      }

      console.log('[FCM Session] FCM token is same ?', newToken === oldToken);

      // New token? Save it.
      if (newToken !== token.value) {
        console.log('[FCM Session] FCM token changed (or new).');
        token.value = newToken;
        localStorage.setItem(LS_TOKEN_KEY, newToken);

        // If user is logged in, tell the backend about the new token
        if (userId.value) {
          // unsubscribe old token first if exists
          if (oldToken) {
            try {
              await firebaseService.unsubscribeFromPushNotification({ fcmToken: oldToken });
              console.log('[FCM Session] Unsubscribed old token from backend.');
            } catch (error) {
              console.warn('[FCM Session] Failed to unsubscribe old token:', error);
            }
          }

          console.log(`[FCM Session] Syncing new token for user: ${userId.value}`);
          await firebaseService.subscribeToPushNotification({ fcmToken: newToken });
        } else {
          console.log('[FCM Session] No user logged in.');
        }
      } else {
        console.log('[FCM Session] FCM token unchanged.');
      }

      // Turn on the foreground listener
      await attachForegroundHandler();
      return token.value;
    } catch (error) {
      console.error('[FCM Session] Failed during push initialization:', error);
      return null;
    }
  };

  /**
   * Call this on login.
   */
  const handleLogin = async (newUserId) => {
    console.log(`[FCM Session] Handling login for user: ${newUserId}`);
    await initializePush({ askPermission: true });

    userId.value = newUserId;
    localStorage.setItem(LS_USER_KEY, newUserId);

    // Link token to user on backend
    await firebaseService.subscribeToPushNotification({ fcmToken: token.value });
    return { userId: userId.value, token: token.value };
  };

  /**
   * Call this on logout.
   */
  const handleLogout = async ({ rotateToken = true } = {}) => {
    console.log('[FCM Session] Handling logout...');
    const oldUser = userId.value;
    const oldToken = token.value;

    // 1. Call API to remove the token/unsubscribe to stop sending messages to this token
    if (oldUser && oldToken) {
      try {
        await firebaseService.unsubscribeFromPushNotification({
          fcmToken: oldToken,
          noLoading: false,
        });
        console.log(`[FCM Session] Disassociated token from user ${oldUser}`);
      } catch (error) {
        console.warn('[FCM Session] Failed to unsubscribe backend.', error);
      }
    }

    // 2. Turn off the foreground listener
    if (foregroundUnsubscribe) {
      try {
        foregroundUnsubscribe(); // Call the unsubscribe function
        console.log('[FCM Session] Detached foreground message handler.');
      } catch (error) {
        console.warn('[FCM Session] Failed to detach foreground handler.', error);
      }
      foregroundUnsubscribe = null; // Clear it
    }

    // 3. Nuke the token. Good for security.
    if (rotateToken) {
      console.log('[FCM Session] Rotating FCM token on logout.');
      try {
        const messaging = await getMessagingSafe();
        const swRegistration = await navigator.serviceWorker.ready;
        if (messaging) {
          // Delete token from Firebase
          await deleteToken(messaging, { serviceWorkerRegistration: swRegistration });
          console.log('[FCM Session] Token deleted from Firebase backend.');
        }
      } catch (error) {
        console.warn('[FCM Session] Failed to delete FCM token:', error);
      }
      // Clear local token
      token.value = null;
      localStorage.removeItem(LS_TOKEN_KEY);
    }

    // 4. Clear local user
    userId.value = null;
    localStorage.removeItem(LS_USER_KEY);
  };

  return {
    // Expose state as readonly, force using functions
    token: readonly(token),
    userId: readonly(userId),

    // Functions
    initializePush,
    handleLogin,
    handleLogout,
  };
};
